// src/components/Alerts/AlertsView.jsx
import React, { useState, useEffect } from 'react';
import { useStore } from '../../store/useStore';
import { alertsApi } from '../../services/api';
import {
  Bell, Plus, Search, RefreshCw, Eye, Edit2, Trash2, X,
  CheckCircle, Clock, AlertTriangle, Zap, Tag, ShieldAlert,
  ChevronRight, Lock, Database, Globe, ShieldCheck, Activity,
  Fingerprint, Layers, Key, Network, FileCheck, Shield,
} from 'lucide-react';
import toast from 'react-hot-toast';

// ── Security categories ───────────────────────────────────────────────────────
const CATEGORIES = {
  'Identity & Access':    { icon: Fingerprint,  color: '#8b5cf6', short: 'IAM' },
  'Data Protection':      { icon: Database,     color: '#3b82f6', short: 'Data' },
  'Network Security':     { icon: Globe,        color: '#06b6d4', short: 'Network' },
  'Compliance':           { icon: FileCheck,    color: '#f59e0b', short: 'Comply' },
  'Zero Trust':           { icon: Lock,         color: '#e879f9', short: 'ZeroTrust' },
  'AKS Security':         { icon: Layers,       color: '#0ea5e9', short: 'AKS' },
  'Monitoring & IR':      { icon: Activity,     color: '#22c55e', short: 'IR' },
  'Infrastructure':       { icon: ShieldAlert,  color: '#64748b', short: 'Infra' },
};

// ── Demo data ─────────────────────────────────────────────────────────────────
const now = Date.now();
const DEMO_ALERTS = [
  // ── Identity & Access Management ──────────────────────────────────────────
  {
    _id: 'sec-iam-1', category: 'Identity & Access',
    title: 'Privilege escalation: service account granted cluster-admin',
    severity: 'CRITICAL', status: 'OPEN', source: 'GCP', service: 'k8s-rbac',
    description: 'Service account "deploy-sa" was bound to cluster-admin ClusterRole at 03:14 UTC. This violates least-privilege policy and was not approved via change-management.',
    rootCause: 'Automated CI pipeline script used overly broad RBAC binding due to missing environment check.',
    aiAnalysis: 'Binding created by CI job ID #8821 via kubectl apply. No change ticket found in ServiceNow for this action. Account previously had only "view" access.',
    recommendation: 'Immediately revoke cluster-admin binding. Restrict CI pipeline to namespace-scoped roles. Add RBAC admission webhook to block cluster-admin bindings.',
    confidence: 97, triggeredAt: new Date(now - 25 * 60000), tags: ['iam', 'rbac', 'k8s', 'privilege-escalation'],
  },
  {
    _id: 'sec-iam-2', category: 'Identity & Access',
    title: 'Service account key >90 days old — rotation overdue',
    severity: 'WARNING', status: 'OPEN', source: 'GCP', service: 'iam',
    description: '3 GCP service account keys have not been rotated in over 90 days, violating the 60-day key rotation policy (PCI DSS Req 8.3).',
    rootCause: 'Key rotation automation job disabled during Q4 infrastructure freeze and not re-enabled.',
    recommendation: 'Enable key rotation cronjob. Rotate all 3 keys immediately. Alert on keys >45 days old.',
    confidence: 99, triggeredAt: new Date(now - 4 * 3600000), tags: ['iam', 'key-rotation', 'pci-dss'],
  },
  {
    _id: 'sec-iam-3', category: 'Identity & Access',
    title: 'MFA bypass attempt: 7 failed challenges for prod-admin',
    severity: 'CRITICAL', status: 'ACKNOWLEDGED', source: 'AZURE', service: 'azure-ad',
    description: 'User prod-admin@acme.com received 7 unexpected MFA push notifications in 4 minutes. Possible MFA fatigue attack.',
    rootCause: 'Attacker obtained password via credential stuffing (password reused from breached service).',
    aiAnalysis: 'Source IPs geo-located to Romania — never seen for this user. Sign-in risk score: HIGH. Azure AD Identity Protection flagged as "Unfamiliar sign-in properties".',
    recommendation: 'Force password reset. Enable number matching MFA. Review Conditional Access policies. Consider Passwordless auth.',
    confidence: 94, triggeredAt: new Date(now - 40 * 60000), acknowledgedBy: 'sec-ops', tags: ['iam', 'mfa', 'brute-force', 'azure-ad'],
  },
  {
    _id: 'sec-iam-4', category: 'Identity & Access',
    title: 'OAuth token leaked in public GitHub repository',
    severity: 'CRITICAL', status: 'OPEN', source: 'GCP', service: 'secret-manager',
    description: 'GitHub secret scanning detected a valid GCP OAuth 2.0 token committed to public repo acme-org/frontend-app in file .env.example.',
    rootCause: 'Developer accidentally committed .env.example with real credentials instead of placeholder values.',
    aiAnalysis: 'Token active and has roles/storage.admin and roles/cloudfunctions.invoker. Commit was public for ~18 minutes before detection.',
    recommendation: 'Immediately revoke token. Rotate associated service account. Add pre-commit hook for secret detection. Audit all GCS access logs for the 18-minute window.',
    confidence: 99, triggeredAt: new Date(now - 12 * 60000), tags: ['iam', 'secret-leak', 'github', 'oauth'],
  },

  // ── Data Protection ────────────────────────────────────────────────────────
  {
    _id: 'sec-data-1', category: 'Data Protection',
    title: 'PII exposure: unmasked SSNs in application logs',
    severity: 'CRITICAL', status: 'OPEN', source: 'GCP', service: 'cloud-logging',
    description: 'Cloud DLP scan detected 142 Social Security Numbers in plaintext within Cloud Logging entries for the checkout service. GDPR/PCI DSS violation.',
    rootCause: 'v2.3.0 introduced verbose error logging that serializes full request body including payment forms.',
    aiAnalysis: 'Matched DLP infoType SSN_US across 142 log entries. Logs retained 30 days — breach window is since deploy 9 days ago.',
    recommendation: 'Immediately disable verbose logging. Purge affected log entries. File breach report if EU citizens affected (GDPR 72h clock). Patch with log sanitization middleware.',
    confidence: 98, triggeredAt: new Date(now - 55 * 60000), tags: ['pii', 'gdpr', 'dlp', 'pci-dss', 'data-leak'],
  },
  {
    _id: 'sec-data-2', category: 'Data Protection',
    title: 'Encryption key expiring in 7 days — Azure Key Vault',
    severity: 'WARNING', status: 'OPEN', source: 'AZURE', service: 'key-vault',
    description: 'RSA-4096 key "prod-db-encryption-key" in Azure Key Vault expires 2026-06-15. All database encryption operations will fail after expiry.',
    rootCause: 'Key was created with a 1-year expiry in 2025 and auto-rotation was not configured.',
    recommendation: 'Enable auto-rotation in Key Vault. Manually rotate key now. Verify all dependent services use key references, not hardcoded versions.',
    confidence: 99, triggeredAt: new Date(now - 2 * 3600000), tags: ['encryption', 'key-vault', 'expiry', 'pci-dss'],
  },
  {
    _id: 'sec-data-3', category: 'Data Protection',
    title: 'MongoDB Atlas: unencrypted backup snapshot detected',
    severity: 'WARNING', status: 'OPEN', source: 'MONGODB', service: 'atlas-backup',
    description: 'Backup snapshot prod-cluster-2026-06-07 was stored without encryption at rest. Violates SOC 2 CC6.1 and internal policy.',
    rootCause: 'Atlas cluster tier was downgraded to M10 which does not support encryption at rest — upgrade removed the feature.',
    recommendation: 'Upgrade cluster to M30+. Enable encryption at rest. Re-run backup. Delete unencrypted snapshot immediately.',
    confidence: 96, triggeredAt: new Date(now - 6 * 3600000), tags: ['mongodb', 'encryption', 'backup', 'soc2'],
  },
  {
    _id: 'sec-data-4', category: 'Data Protection',
    title: 'PostgreSQL: pg_dump backup missing for 3 consecutive days',
    severity: 'WARNING', status: 'ACKNOWLEDGED', source: 'DATABASE', service: 'backup-agent',
    description: 'Automated pg_dump backup job has not completed successfully since 2026-06-05. RTO/RPO commitments at risk.',
    rootCause: 'Backup agent disk quota exhausted. Prior run created 2× expected backup size due to un-vacuumed bloat.',
    recommendation: 'Free disk space. Run VACUUM FULL on bloated tables. Fix backup rotation to keep only 7 days. Set up backup alerting.',
    confidence: 91, triggeredAt: new Date(now - 3 * 24 * 3600000), acknowledgedBy: 'dba-team', tags: ['backup', 'database', 'rpo', 'soc2'],
  },

  // ── Network Security ───────────────────────────────────────────────────────
  {
    _id: 'sec-net-1', category: 'Network Security',
    title: 'Firewall rule change: port 22 opened to 0.0.0.0/0',
    severity: 'CRITICAL', status: 'OPEN', source: 'GCP', service: 'vpc-firewall',
    description: 'A GCP VPC firewall rule was modified at 11:47 UTC to allow SSH (port 22) from all source IPs. No change ticket found. CIS Benchmark violation.',
    rootCause: 'Engineer temporarily opened port for debugging and forgot to close. Change not tracked in ITSM.',
    aiAnalysis: 'Rule "allow-ssh-debug" created by user eng-user@acme.com. Affected instances: prod-api-01, prod-api-02, prod-db-01 (12 VMs total).',
    recommendation: 'Immediately delete or restrict the firewall rule. Require MFA + change approval for all firewall modifications. Enable Cloud Audit Logs alerting for firewall changes.',
    confidence: 99, triggeredAt: new Date(now - 18 * 60000), tags: ['firewall', 'ssh', 'network', 'cis-benchmark'],
  },
  {
    _id: 'sec-net-2', category: 'Network Security',
    title: 'Port scan detected: 4,200 probes from 185.220.101.x',
    severity: 'WARNING', status: 'OPEN', source: 'AZURE', service: 'azure-defender',
    description: 'Microsoft Defender for Cloud detected systematic port scanning from known Tor exit node 185.220.101.47. 4,200 probes over 8 minutes targeting ports 22, 3306, 5432, 6379.',
    aiAnalysis: 'Source IP belongs to Tor network (confirmed via threat intel). Targeting ports suggest reconnaissance for exposed databases and caches. No successful connections.',
    recommendation: 'Block 185.220.101.0/24 in NSG. Enable Just-In-Time VM access. Ensure no database ports exposed to internet. Review Azure DDoS Protection settings.',
    confidence: 88, triggeredAt: new Date(now - 35 * 60000), tags: ['port-scan', 'tor', 'reconnaissance', 'network'],
  },
  {
    _id: 'sec-net-3', category: 'Network Security',
    title: 'TLS 1.0 connection accepted — legacy cipher deprecation required',
    severity: 'WARNING', status: 'OPEN', source: 'AZURE', service: 'app-gateway',
    description: 'Azure Application Gateway is accepting TLS 1.0 connections from 3 client IPs. TLS 1.0 is prohibited by PCI DSS 3.2.1 since June 2018.',
    rootCause: 'Application Gateway SSL policy was not updated during last year\'s compliance review.',
    recommendation: 'Set Azure Application Gateway SSL policy to AppGwSslPolicy20220101S (TLS 1.2+ only). Test all client integrations before applying.',
    confidence: 95, triggeredAt: new Date(now - 1.5 * 3600000), tags: ['tls', 'pci-dss', 'cryptography', 'network'],
  },
  {
    _id: 'sec-net-4', category: 'Network Security',
    title: 'DDoS traffic spike: 2.4 Gbps ingress — mitigation active',
    severity: 'CRITICAL', status: 'ACKNOWLEDGED', source: 'AZURE', service: 'azure-ddos',
    description: 'Azure DDoS Protection Standard detected and is actively mitigating a volumetric attack. 2.4 Gbps ingress vs baseline of 120 Mbps. Mitigation policy engaged.',
    aiAnalysis: 'UDP flood from distributed botnet (2,400 source IPs). Target: public load balancer IP. Azure DDoS telemetry shows 94% mitigation effectiveness. User traffic nominal.',
    recommendation: 'Monitor mitigation. Alert security team. Review Azure DDoS Protection plan tier. Post-incident: analyze source IP patterns and update WAF rules.',
    confidence: 99, triggeredAt: new Date(now - 8 * 60000), acknowledgedBy: 'netsec-on-call', tags: ['ddos', 'network', 'mitigation', 'azure'],
  },

  // ── Compliance (PCI DSS / SOC 2) ──────────────────────────────────────────
  {
    _id: 'sec-comp-1', category: 'Compliance',
    title: 'PCI DSS: cardholder data found outside PCI scope boundary',
    severity: 'CRITICAL', status: 'OPEN', source: 'DATABASE', service: 'prod-pg-01',
    description: 'Automated DLP scan found 1,204 rows containing full PAN (Primary Account Numbers) in table analytics.user_events — outside the designated PCI DSS CDE scope.',
    rootCause: 'Analytics pipeline joins payment events without tokenizing PAN before writing to analytics DB.',
    aiAnalysis: 'Table analytics.user_events contains column "raw_card_number" populated since 2026-03-12. 1,204 distinct PAN values. Analytics DB does not meet PCI DSS requirements (no HSM, broader access).',
    recommendation: 'Immediately truncate/mask analytics.user_events.raw_card_number. Fix ETL pipeline to use tokenized PAN only. Conduct scope re-assessment with QSA.',
    confidence: 98, triggeredAt: new Date(now - 90 * 60000), tags: ['pci-dss', 'pan', 'cde', 'compliance', 'dlp'],
  },
  {
    _id: 'sec-comp-2', category: 'Compliance',
    title: 'SOC 2 CC6.1: access review overdue for 14 privileged accounts',
    severity: 'WARNING', status: 'OPEN', source: 'AZURE', service: 'azure-ad',
    description: '14 users with privileged roles (Global Admin, Security Admin) have not completed quarterly access review due 2026-05-31. SOC 2 Type II audit starts 2026-07-01.',
    recommendation: 'Trigger immediate access review in Azure AD Identity Governance. Revoke access for users who do not respond within 48h. Update access review schedule to monthly.',
    confidence: 99, triggeredAt: new Date(now - 5 * 24 * 3600000), tags: ['soc2', 'access-review', 'iam', 'audit'],
  },
  {
    _id: 'sec-comp-3', category: 'Compliance',
    title: 'GDPR: data retention policy violation — user records retained >3 years',
    severity: 'WARNING', status: 'OPEN', source: 'MONGODB', service: 'mongodb/prod.users',
    description: 'MongoDB Atlas data retention job failed silently. 28,401 user documents older than 3 years (retention policy) remain in prod.users collection. GDPR Art. 5(1)(e) violation risk.',
    rootCause: 'TTL index on "lastActiveAt" was dropped during Atlas cluster migration in March 2026.',
    recommendation: 'Re-create TTL index: db.users.createIndex({"lastActiveAt":1}, {expireAfterSeconds: 94608000}). Run one-time deletion of over-retained records. Log deletion for compliance record.',
    confidence: 91, triggeredAt: new Date(now - 2 * 24 * 3600000), tags: ['gdpr', 'data-retention', 'compliance', 'mongodb'],
  },

  // ── Zero Trust ─────────────────────────────────────────────────────────────
  {
    _id: 'sec-zt-1', category: 'Zero Trust',
    title: 'Lateral movement: api-service accessing secrets it has never accessed',
    severity: 'CRITICAL', status: 'OPEN', source: 'GCP', service: 'secret-manager',
    description: 'GCP Secret Manager audit logs show api-service accessing 12 secrets outside its normal access pattern — including db-admin-password and payment-api-key.',
    aiAnalysis: 'Behavioral baseline: api-service normally accesses 2 secrets (jwt-secret, redis-password). New access pattern detected at 04:22 UTC — 12 secrets accessed in 3 minutes. Possible container compromise.',
    recommendation: 'Isolate api-service pod immediately. Rotate all 12 accessed secrets. Forensically analyze pod filesystem and process tree. Review Secret Manager IAM bindings.',
    confidence: 91, triggeredAt: new Date(now - 30 * 60000), tags: ['zero-trust', 'lateral-movement', 'secrets', 'compromise'],
  },
  {
    _id: 'sec-zt-2', category: 'Zero Trust',
    title: 'Unmanaged device accessing production APIs from corporate network',
    severity: 'WARNING', status: 'OPEN', source: 'AZURE', service: 'azure-ad',
    description: 'Conditional Access blocked 3 sign-in attempts to production Azure resources from a device not enrolled in Intune MDM. Device compliance state: Unknown.',
    recommendation: 'Enforce Intune enrollment for all corporate devices. Strengthen Conditional Access policy to require compliant device + MFA. Investigate whether this is a BYOD violation.',
    confidence: 84, triggeredAt: new Date(now - 45 * 60000), tags: ['zero-trust', 'device-compliance', 'conditional-access', 'intune'],
  },
  {
    _id: 'sec-zt-3', category: 'Zero Trust',
    title: 'Service mesh: east-west traffic bypassing mTLS policy',
    severity: 'WARNING', status: 'ACKNOWLEDGED', source: 'GCP', service: 'istio',
    description: 'Istio audit detected 3 service-to-service connections in the prod namespace bypassing mutual TLS — traffic sent in plaintext. Violates Zero Trust network segmentation policy.',
    rootCause: 'PeerAuthentication policy was not applied to new services deployed last week. Default permissive mode allows plaintext fallback.',
    recommendation: 'Apply PeerAuthentication with mode STRICT to all namespaces. Use Istio AuthorizationPolicy to deny plaintext. Add policy compliance check to CI/CD pipeline.',
    confidence: 93, triggeredAt: new Date(now - 3 * 3600000), acknowledgedBy: 'platform-team', tags: ['zero-trust', 'mtls', 'istio', 'service-mesh'],
  },

  // ── AKS Security ──────────────────────────────────────────────────────────
  {
    _id: 'sec-aks-1', category: 'AKS Security',
    title: 'Privileged container running in prod namespace',
    severity: 'CRITICAL', status: 'OPEN', source: 'AZURE', service: 'aks/nodepool1',
    description: 'Pod "debug-tools-7f8b9" in prod namespace is running with securityContext.privileged=true. Privileged containers have full access to host kernel and can escape to host node.',
    rootCause: 'Debug pod deployed by engineer for incident investigation was not removed after use.',
    aiAnalysis: 'Pod running for 4h 22m. Image: alpine:latest (unscanned). No resource limits. Mounted /proc and /sys from host. Violates CIS AKS Benchmark 5.2.1.',
    recommendation: 'Immediately delete the privileged pod. Enable Pod Security Standards (restricted profile) on prod namespace. Add OPA Gatekeeper policy to block privileged containers.',
    confidence: 99, triggeredAt: new Date(now - 22 * 60000), tags: ['aks', 'privileged-container', 'k8s-security', 'cis-benchmark'],
  },
  {
    _id: 'sec-aks-2', category: 'AKS Security',
    title: 'AKS: cluster nodes running outdated Kubernetes version (1.26.x)',
    severity: 'WARNING', status: 'OPEN', source: 'AZURE', service: 'aks/nodepool1',
    description: 'AKS cluster prod-aks-01 is running Kubernetes 1.26.12 which reached end-of-life in 2024. 14 CVEs patched in 1.28 and 1.29 remain unpatched.',
    rootCause: 'Cluster upgrade was deferred due to API deprecations in payment service. Upgrade window never rescheduled.',
    recommendation: 'Schedule upgrade to 1.29 during next maintenance window. Fix API deprecations in payment service first. Enable AKS automatic upgrade channel "patch".',
    confidence: 97, triggeredAt: new Date(now - 7 * 24 * 3600000), tags: ['aks', 'kubernetes', 'cve', 'upgrade', 'azure'],
  },
  {
    _id: 'sec-aks-3', category: 'AKS Security',
    title: 'Kubernetes secrets stored unencrypted in etcd',
    severity: 'WARNING', status: 'OPEN', source: 'AZURE', service: 'aks/etcd',
    description: 'AKS cluster prod-aks-01 does not have etcd encryption at rest enabled. 847 Kubernetes Secrets including TLS certificates and database passwords stored in plaintext in etcd.',
    rootCause: 'Encryption at rest was not enabled at cluster creation. AKS requires cluster recreation or enabling the feature via cluster upgrade.',
    recommendation: 'Enable AKS etcd encryption via Azure Key Vault KMS plugin. Consider migrating to Azure Key Vault CSI driver for secret delivery instead of K8s Secrets.',
    confidence: 95, triggeredAt: new Date(now - 2 * 24 * 3600000), tags: ['aks', 'etcd', 'encryption', 'secrets', 'k8s-security'],
  },
  {
    _id: 'sec-aks-4', category: 'AKS Security',
    title: 'Container image with CRITICAL CVEs deployed to production',
    severity: 'CRITICAL', status: 'OPEN', source: 'AZURE', service: 'aks/payments',
    description: 'Trivy scan detected 3 CRITICAL CVEs in image payments-service:2.1.4 currently running in production: CVE-2024-3094 (XZ backdoor, CVSS 10.0), CVE-2023-44487 (HTTP/2 RapidReset).',
    aiAnalysis: 'Image pushed to ACR without passing security gate. CI pipeline security scan step was bypassed via --skip-scan flag. Image running in 4 replicas.',
    recommendation: 'Immediately roll back to payments-service:2.1.3. Remove --skip-scan from CI pipeline. Enable AKS Defender image vulnerability assessment. Block deployment if CRITICAL CVEs found.',
    confidence: 99, triggeredAt: new Date(now - 15 * 60000), tags: ['aks', 'cve', 'container-security', 'trivy', 'ci-cd'],
  },

  // ── Monitoring & Incident Response ─────────────────────────────────────────
  {
    _id: 'sec-ir-1', category: 'Monitoring & IR',
    title: 'SIEM correlation: brute-force → privilege escalation kill chain',
    severity: 'CRITICAL', status: 'OPEN', source: 'AZURE', service: 'microsoft-sentinel',
    description: 'Microsoft Sentinel correlated 3 events into a kill chain: (1) brute-force on VPN, (2) successful auth 18 min later, (3) privilege escalation attempt 4 min after that.',
    aiAnalysis: 'Attack sequence: 847 failed VPN logins from 91.108.56.x → successful login admin-temp account → kubectl get clusterrolebindings within 4 minutes. High-confidence APT pattern.',
    recommendation: 'Block source IP range. Disable admin-temp account. Initiate Incident Response playbook IR-003 (Compromised Admin Account). Forensic investigation of all kubectl commands.',
    confidence: 96, triggeredAt: new Date(now - 10 * 60000), tags: ['siem', 'kill-chain', 'apt', 'incident-response', 'sentinel'],
  },
  {
    _id: 'sec-ir-2', category: 'Monitoring & IR',
    title: 'Alert fatigue: 2,400 low-fidelity alerts suppressed this week',
    severity: 'WARNING', status: 'OPEN', source: 'GCP', service: 'alerting',
    description: '2,400 alerts auto-suppressed in the last 7 days due to noise thresholds. Review suggests suppression rules may be hiding real incidents.',
    recommendation: 'Audit suppression rules. Implement ML-based alert correlation. Review MTTD/MTTR metrics. Consider SOAR playbook automation for common patterns.',
    confidence: 72, triggeredAt: new Date(now - 24 * 3600000), tags: ['monitoring', 'alert-fatigue', 'soc', 'ir'],
  },

  // ── Legacy infrastructure alerts ──────────────────────────────────────────
  {
    _id: 'da-1', category: 'Infrastructure',
    title: 'Memory pressure: Cloud Run instance OOM killed',
    severity: 'CRITICAL', status: 'OPEN', source: 'GCP', service: 'api-gateway',
    description: 'Instance exceeded 512MB memory limit and was terminated. Auto-restart in progress.',
    rootCause: 'Memory leak in request caching layer — eviction disabled after v2.4.1 deploy.',
    aiAnalysis: 'Heap profiling shows object retention in LRU cache. 847MB allocated vs 512MB limit.',
    recommendation: 'Roll back to v2.4.0 or hotfix: disable cache warming on startup.',
    confidence: 92, triggeredAt: new Date(now - 8 * 60000), tags: ['memory', 'oom', 'critical'],
  },
  {
    _id: 'da-2', category: 'Infrastructure',
    title: 'SSL Certificate expiring in 3 days — auto-renewal failed',
    severity: 'CRITICAL', status: 'OPEN', source: 'AZURE', service: 'web-frontend',
    description: 'TLS certificate for api.acme.com expires in 72h. ACME challenge failing.',
    rootCause: 'DNS CAA record allows digicert.com but renewal uses letsencrypt.org.',
    recommendation: 'Add CAA record for letsencrypt.org or provision certificate via DigiCert manually.',
    confidence: 97, triggeredAt: new Date(now - 2 * 3600000), tags: ['ssl', 'certificate', 'expiry'],
  },
  {
    _id: 'da-5', category: 'Infrastructure',
    title: 'API p99 latency degraded: 1.2s → 3.8s on /payments',
    severity: 'WARNING', status: 'OPEN', source: 'GCP', service: 'api-gateway',
    description: 'p99 response time for /api/v2/payments increased 3× in the last 15 minutes.',
    rootCause: 'Redis cache cold after restart — miss rate at 78%, declining.',
    recommendation: 'Monitor — expected full recovery in ~10 min. Consider pre-warming on restart.',
    confidence: 78, triggeredAt: new Date(now - 20 * 60000), tags: ['latency', 'cache', 'redis'],
  },
  {
    _id: 'da-7', category: 'Infrastructure',
    title: 'MongoDB replication lag: 4.2s on secondary-1',
    severity: 'WARNING', status: 'OPEN', source: 'MONGODB', service: 'db-connector',
    description: 'Secondary replica falling 4.2s behind primary. Stale reads possible.',
    rootCause: 'Heavy batch import job overwhelming secondary oplog replay capacity.',
    recommendation: 'Pause batch import or schedule off-peak. Consider adding a hidden secondary.',
    confidence: 74, triggeredAt: new Date(now - 12 * 60000), tags: ['mongodb', 'replication', 'lag'],
  },
  {
    _id: 'da-11', category: 'Infrastructure',
    title: 'GCP auto-scaling triggered: replicas 3 → 8 (CPU=78%)',
    severity: 'WARNING', status: 'RESOLVED', source: 'GCP', service: 'stream-processor',
    description: 'Horizontal pod autoscaler triggered on CPU spike. Scale-out completed successfully.',
    triggeredAt: new Date(now - 3 * 3600000), resolvedAt: new Date(now - 2.5 * 3600000), tags: ['autoscaling', 'cpu', 'k8s'],
  },
];

// ── Constants ─────────────────────────────────────────────────────────────────
const SEV_COLORS = { CRITICAL: '#ef4444', WARNING: '#f59e0b', INFO: '#3b82f6' };
const SEV_BG     = { CRITICAL: 'rgba(239,68,68,.15)', WARNING: 'rgba(245,158,11,.15)', INFO: 'rgba(59,130,246,.15)' };
const SEV_ICONS  = { CRITICAL: '🔴', WARNING: '🟡', INFO: '🔵' };
const SRC_COLORS = { GCP: '#4285f4', AZURE: '#0078d4', MONGODB: '#4ade80', DATABASE: '#a78bfa', AI_AGENT: '#8b5cf6' };
const STATUS_STYLES = {
  OPEN:         { color: '#ef4444', bg: 'rgba(239,68,68,.15)',  label: 'Open' },
  ACKNOWLEDGED: { color: '#f59e0b', bg: 'rgba(245,158,11,.15)', label: 'Acknowledged' },
  RESOLVED:     { color: '#22c55e', bg: 'rgba(34,197,94,.15)',  label: 'Resolved' },
  SUPPRESSED:   { color: '#64748b', bg: 'rgba(100,116,139,.2)', label: 'Suppressed' },
};
const SOURCES   = ['GCP', 'AZURE', 'MONGODB', 'DATABASE', 'AI_AGENT'];
const EMPTY_FORM = { title: '', description: '', severity: 'WARNING', status: 'OPEN', source: 'GCP', service: '', category: 'Infrastructure', tags: '', aiAnalysis: '', rootCause: '', recommendation: '', confidence: '' };

// ── Helpers ───────────────────────────────────────────────────────────────────
function timeAgo(date) {
  if (!date) return '—';
  const d = Date.now() - new Date(date).getTime();
  if (d < 60000)    return `${Math.floor(d / 1000)}s ago`;
  if (d < 3600000)  return `${Math.floor(d / 60000)}m ago`;
  if (d < 86400000) return `${Math.floor(d / 3600000)}h ago`;
  return `${Math.floor(d / 86400000)}d ago`;
}

function CategoryBadge({ category, size = 'sm' }) {
  const meta  = CATEGORIES[category] || CATEGORIES['Infrastructure'];
  const Icon  = meta.icon;
  const small = size === 'xs';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: small ? 3 : 4,
      padding: small ? '1px 6px' : '2px 8px',
      borderRadius: 4, fontSize: small ? 9 : 10, fontWeight: 700,
      background: `${meta.color}18`, color: meta.color, border: `1px solid ${meta.color}35`,
    }}>
      <Icon size={small ? 8 : 9} />
      {small ? meta.short : category}
    </span>
  );
}

// ── Modal wrapper ─────────────────────────────────────────────────────────────
function Modal({ title, wide, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,.65)', backdropFilter: 'blur(4px)' }}>
      <div className="relative w-full rounded-xl flex flex-col"
        style={{ maxWidth: wide ? 820 : 560, maxHeight: '92vh', background: 'var(--bg-secondary)', border: '1px solid var(--border)', boxShadow: '0 25px 60px rgba(0,0,0,.6)' }}>
        <div className="flex items-center justify-between px-5 py-4 shrink-0" style={{ borderBottom: '1px solid var(--border)' }}>
          <h2 className="text-sm font-semibold">{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', lineHeight: 0 }} className="hover:text-white"><X size={16} /></button>
        </div>
        <div className="overflow-y-auto flex-1 px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

const inputSx = { width: '100%', padding: '8px 10px', borderRadius: 6, fontSize: 13, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' };
function Field({ label, children }) {
  return (
    <div className="mb-4">
      <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-2)' }}>{label}</label>
      {children}
    </div>
  );
}

// ── Alert Detail ──────────────────────────────────────────────────────────────
function AlertDetail({ alert, onClose, onEdit }) {
  const { updateAlert, removeAlert } = useStore();
  const sc  = SEV_COLORS[alert.severity]  || '#64748b';
  const st  = STATUS_STYLES[alert.status] || STATUS_STYLES.OPEN;
  const cat = CATEGORIES[alert.category]  || CATEGORIES['Infrastructure'];
  const CatIcon = cat.icon;

  async function ack()     { try { const u = await alertsApi.acknowledge(alert._id, 'frontend-user'); updateAlert(u); toast.success('Acknowledged'); onClose(); } catch { toast.error('Failed'); } }
  async function resolve() { try { const u = await alertsApi.resolve(alert._id); updateAlert(u); toast.success('Resolved'); onClose(); } catch { toast.error('Failed'); } }
  async function del()     { if (!confirm('Delete this alert?')) return; try { await alertsApi.delete(alert._id); removeAlert(alert._id); toast.success('Deleted'); onClose(); } catch { toast.error('Failed'); } }

  return (
    <Modal title="Alert Detail" wide onClose={onClose}>
      {/* Severity + category header */}
      <div className="rounded-xl p-4 mb-4 flex items-start gap-3"
        style={{ background: `${sc}10`, border: `1px solid ${sc}30` }}>
        <span className="text-2xl">{SEV_ICONS[alert.severity] || '⚪'}</span>
        <div className="flex-1 min-w-0">
          <div className="text-base font-bold leading-snug mb-2">{alert.title}</div>
          <div className="flex items-center gap-2 flex-wrap">
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: SEV_BG[alert.severity], color: sc }}>{alert.severity}</span>
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: st.bg, color: st.color }}>{st.label}</span>
            {alert.category && <CategoryBadge category={alert.category} />}
            {alert.source  && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: `${SRC_COLORS[alert.source] || '#64748b'}18`, color: SRC_COLORS[alert.source] || '#64748b' }}>{alert.source}</span>}
            {alert.service && <span className="text-xs mono" style={{ color: 'var(--text-3)' }}>{alert.service}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={onEdit} style={{ padding: '5px 10px', borderRadius: 5, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
            <Edit2 size={11} /> Edit
          </button>
          <button onClick={del} style={{ padding: '5px 10px', borderRadius: 5, background: 'rgba(239,68,68,.15)', border: '1px solid rgba(239,68,68,.3)', color: '#f87171', cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
            <Trash2 size={11} /> Delete
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Left */}
        <div>
          {alert.description && (
            <div className="mb-4">
              <div className="text-xs font-semibold mb-1.5" style={{ color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Description</div>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-2)' }}>{alert.description}</p>
            </div>
          )}

          {/* Compliance/Category context */}
          {alert.category && (
            <div className="rounded-lg p-3 mb-4 flex items-start gap-2"
              style={{ background: `${cat.color}08`, border: `1px solid ${cat.color}25` }}>
              <CatIcon size={13} style={{ color: cat.color, marginTop: 1, flexShrink: 0 }} />
              <div>
                <div className="text-xs font-semibold mb-0.5" style={{ color: cat.color }}>{alert.category}</div>
                <div className="text-xs leading-relaxed" style={{ color: 'var(--text-3)' }}>
                  {CAT_CONTEXT[alert.category] || 'Security category alert'}
                </div>
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="mb-4">
            <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Timeline</div>
            <div className="space-y-2">
              <TimelineItem icon={ShieldAlert}   color="#ef4444" label="Triggered"          value={alert.triggeredAt  ? new Date(alert.triggeredAt).toLocaleString()  : '—'} />
              {alert.acknowledgedBy && <TimelineItem icon={Clock}       color="#f59e0b" label={`Acknowledged by ${alert.acknowledgedBy}`} value="" />}
              {alert.resolvedAt    && <TimelineItem icon={CheckCircle}  color="#22c55e" label="Resolved"             value={new Date(alert.resolvedAt).toLocaleString()} />}
            </div>
          </div>

          {/* Tags */}
          {(alert.tags || []).length > 0 && (
            <div>
              <div className="text-xs font-semibold mb-1.5" style={{ color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Tags</div>
              <div className="flex flex-wrap gap-1.5">
                {alert.tags.map(t => (
                  <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, padding: '2px 8px', borderRadius: 4, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-3)' }}>
                    <Tag size={8} /> {t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right — AI blocks */}
        <div className="space-y-3">
          {alert.rootCause      && <InfoBlock color="#ef4444" icon={AlertTriangle} title="Root Cause">{alert.rootCause}</InfoBlock>}
          {alert.aiAnalysis     && <InfoBlock color="#8b5cf6" icon={Zap}           title="AI Analysis">{alert.aiAnalysis}</InfoBlock>}
          {alert.recommendation && <InfoBlock color="#22c55e" icon={CheckCircle}   title="Recommendation">{alert.recommendation}</InfoBlock>}
          {alert.confidence != null && (
            <div className="p-3 rounded-lg" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}>
              <div className="flex items-center justify-between mb-1.5 text-xs">
                <span style={{ color: 'var(--text-3)', fontWeight: 600 }}>AI Confidence</span>
                <span className="font-bold" style={{ color: alert.confidence >= 80 ? '#22c55e' : alert.confidence >= 60 ? '#f59e0b' : '#ef4444' }}>{alert.confidence}%</span>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--bg-primary)' }}>
                <div style={{ width: `${alert.confidence}%`, height: '100%', borderRadius: 99, background: alert.confidence >= 80 ? '#22c55e' : alert.confidence >= 60 ? '#f59e0b' : '#ef4444', transition: 'width .5s ease' }} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      {alert.status === 'OPEN' && (
        <div className="flex gap-2 mt-4 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
          <button onClick={ack}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 500, background: 'rgba(245,158,11,.15)', border: '1px solid rgba(245,158,11,.3)', color: '#fbbf24', cursor: 'pointer' }}>
            <Clock size={13} /> Acknowledge
          </button>
          <button onClick={resolve}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 500, background: 'rgba(34,197,94,.15)', border: '1px solid rgba(34,197,94,.3)', color: '#4ade80', cursor: 'pointer' }}>
            <CheckCircle size={13} /> Resolve
          </button>
        </div>
      )}
      {alert.status === 'ACKNOWLEDGED' && (
        <div className="flex gap-2 mt-4 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
          <button onClick={resolve}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 500, background: 'rgba(34,197,94,.15)', border: '1px solid rgba(34,197,94,.3)', color: '#4ade80', cursor: 'pointer' }}>
            <CheckCircle size={13} /> Resolve
          </button>
        </div>
      )}
    </Modal>
  );
}

const CAT_CONTEXT = {
  'Identity & Access':  'Tracks authentication, authorization, service account management, and privileged access violations.',
  'Data Protection':    'Monitors encryption, PII/PAN exposure, data retention compliance, and backup integrity.',
  'Network Security':   'Detects firewall changes, port scans, DDoS attacks, and insecure protocol usage.',
  'Compliance':         'Enforces PCI DSS, SOC 2, GDPR, and internal policy requirements with automated auditing.',
  'Zero Trust':         'Detects lateral movement, device compliance violations, and unverified east-west traffic.',
  'AKS Security':       'Monitors Kubernetes security posture: privileged containers, CVEs, RBAC, etcd encryption.',
  'Monitoring & IR':    'Incident response: kill-chain detection, alert correlation, and SIEM integration.',
  'Infrastructure':     'Platform-level alerts: uptime, performance degradation, and capacity issues.',
};

function TimelineItem({ icon: Icon, color, label, value }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <Icon size={11} style={{ color, flexShrink: 0 }} />
      <span style={{ color: 'var(--text-2)' }}>{label}</span>
      {value && <span className="ml-auto mono" style={{ color: 'var(--text-3)' }}>{value}</span>}
    </div>
  );
}

function InfoBlock({ color, icon: Icon, title, children }) {
  return (
    <div className="p-3 rounded-lg" style={{ background: `${color}08`, border: `1px solid ${color}25` }}>
      <div className="flex items-center gap-1.5 text-xs font-semibold mb-1.5" style={{ color }}>
        <Icon size={11} /> {title}
      </div>
      <p className="text-xs leading-relaxed" style={{ color: 'var(--text-2)' }}>{children}</p>
    </div>
  );
}

// ── Create / Edit form ────────────────────────────────────────────────────────
function AlertForm({ alert, onClose, onSaved }) {
  const [form, setForm] = useState(alert
    ? { title: alert.title, description: alert.description || '', severity: alert.severity, status: alert.status, source: alert.source || 'GCP', service: alert.service || '', category: alert.category || 'Infrastructure', tags: (alert.tags || []).join(', '), aiAnalysis: alert.aiAnalysis || '', rootCause: alert.rootCause || '', recommendation: alert.recommendation || '', confidence: alert.confidence ?? '' }
    : EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  async function save() {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form, tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [], confidence: form.confidence !== '' ? Number(form.confidence) : undefined };
      let saved;
      if (alert?._id) saved = await alertsApi.update(alert._id, payload);
      else            saved = await alertsApi.create(payload);
      onSaved(saved, !!alert);
      onClose();
      toast.success(alert ? 'Alert updated' : 'Alert created');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  }

  return (
    <Modal title={alert ? 'Edit Alert' : 'New Alert'} wide onClose={onClose}>
      <div className="grid grid-cols-2 gap-x-4">
        <div className="col-span-2">
          <Field label="Title *">
            <input style={inputSx} value={form.title} onChange={e => f('title', e.target.value)} placeholder="e.g. Privileged container running in prod namespace" />
          </Field>
        </div>

        {/* Severity visual picker */}
        <Field label="Severity">
          <div className="flex gap-2">
            {['CRITICAL','WARNING','INFO'].map(s => (
              <button key={s} onClick={() => f('severity', s)}
                className="flex-1 py-2 rounded text-xs font-bold"
                style={{ background: form.severity === s ? SEV_BG[s] : 'var(--bg-tertiary)', color: form.severity === s ? SEV_COLORS[s] : 'var(--text-3)', border: `1px solid ${form.severity === s ? SEV_COLORS[s] + '50' : 'var(--border)'}`, cursor: 'pointer' }}>
                {SEV_ICONS[s]} {s}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Status">
          <select style={{ ...inputSx, cursor: 'pointer' }} value={form.status} onChange={e => f('status', e.target.value)}>
            {Object.entries(STATUS_STYLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </Field>

        {/* Security Category picker — full width */}
        <div className="col-span-2">
          <Field label="Security Category">
            <div className="flex flex-wrap gap-2">
              {Object.entries(CATEGORIES).map(([cat, meta]) => {
                const Icon = meta.icon;
                const active = form.category === cat;
                return (
                  <button key={cat} onClick={() => f('category', cat)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold"
                    style={{ background: active ? `${meta.color}20` : 'var(--bg-tertiary)', color: active ? meta.color : 'var(--text-3)', border: `1px solid ${active ? meta.color + '50' : 'var(--border)'}`, cursor: 'pointer' }}>
                    <Icon size={11} /> {cat}
                  </button>
                );
              })}
            </div>
          </Field>
        </div>

        <Field label="Source">
          <select style={{ ...inputSx, cursor: 'pointer' }} value={form.source} onChange={e => f('source', e.target.value)}>
            <option value="">None</option>
            {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Service">
          <input style={inputSx} value={form.service} onChange={e => f('service', e.target.value)} placeholder="e.g. aks/nodepool1" />
        </Field>

        <div className="col-span-2">
          <Field label="Description">
            <textarea style={{ ...inputSx, resize: 'vertical', minHeight: 64 }} value={form.description} onChange={e => f('description', e.target.value)} placeholder="What happened? What is the security impact?" />
          </Field>
        </div>
        <div className="col-span-2">
          <Field label="Root Cause">
            <textarea style={{ ...inputSx, resize: 'vertical', minHeight: 56 }} value={form.rootCause} onChange={e => f('rootCause', e.target.value)} placeholder="What caused this alert?" />
          </Field>
        </div>
        <div className="col-span-2">
          <Field label="AI Analysis">
            <textarea style={{ ...inputSx, resize: 'vertical', minHeight: 56 }} value={form.aiAnalysis} onChange={e => f('aiAnalysis', e.target.value)} placeholder="AI-generated threat analysis…" />
          </Field>
        </div>
        <div className="col-span-2">
          <Field label="Recommendation">
            <textarea style={{ ...inputSx, resize: 'vertical', minHeight: 56 }} value={form.recommendation} onChange={e => f('recommendation', e.target.value)} placeholder="Remediation steps…" />
          </Field>
        </div>
        <Field label="AI Confidence (0–100)">
          <input style={inputSx} type="number" min="0" max="100" value={form.confidence} onChange={e => f('confidence', e.target.value)} placeholder="e.g. 94" />
        </Field>
        <Field label="Tags (comma-separated)">
          <input style={inputSx} value={form.tags} onChange={e => f('tags', e.target.value)} placeholder="e.g. pci-dss, aks, privilege-escalation" />
        </Field>
      </div>
      <div className="flex justify-end gap-2 mt-2">
        <button onClick={onClose} style={{ padding: '7px 16px', borderRadius: 6, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
        <button onClick={save} disabled={saving || !form.title.trim()}
          style={{ padding: '7px 16px', borderRadius: 6, background: 'rgba(59,130,246,.2)', border: '1px solid rgba(59,130,246,.4)', color: '#60a5fa', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: saving || !form.title.trim() ? .4 : 1 }}>
          {saving ? 'Saving…' : alert ? 'Save Changes' : 'Create Alert'}
        </button>
      </div>
    </Modal>
  );
}

// ── Alert Card ────────────────────────────────────────────────────────────────
function AlertCard({ alert, onView, onEdit }) {
  const { updateAlert, removeAlert } = useStore();
  const sc = SEV_COLORS[alert.severity]  || '#64748b';
  const st = STATUS_STYLES[alert.status] || STATUS_STYLES.OPEN;

  async function ack(e)     { e.stopPropagation(); try { const u = await alertsApi.acknowledge(alert._id, 'frontend-user'); updateAlert(u); toast.success('Acknowledged'); } catch { toast.error('Failed'); } }
  async function resolve(e) { e.stopPropagation(); try { const u = await alertsApi.resolve(alert._id); updateAlert(u); toast.success('Resolved'); } catch { toast.error('Failed'); } }
  async function del(e)     { e.stopPropagation(); if (!confirm('Delete this alert?')) return; try { await alertsApi.delete(alert._id); removeAlert(alert._id); toast.success('Deleted'); } catch { toast.error('Failed'); } }

  return (
    <div onClick={onView}
      className="group flex gap-3 rounded-xl p-4 cursor-pointer hover:bg-white/[0.02] transition-colors"
      style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderLeft: `3px solid ${sc}` }}>
      <span className="text-xl shrink-0 mt-0.5">{SEV_ICONS[alert.severity] || '⚪'}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2 mb-1">
          <span className="text-sm font-semibold leading-snug flex-1">{alert.title}</span>
          <ChevronRight size={14} style={{ color: 'var(--text-3)', flexShrink: 0, marginTop: 2, opacity: 0 }} className="group-hover:opacity-100 transition-opacity" />
        </div>
        {alert.description && (
          <p className="text-xs mb-2 line-clamp-1" style={{ color: 'var(--text-3)' }}>{alert.description}</p>
        )}
        {alert.rootCause && (
          <p className="text-xs mb-2" style={{ color: '#a78bfa' }}>🤖 {alert.rootCause.slice(0, 90)}{alert.rootCause.length > 90 ? '…' : ''}</p>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 7px', borderRadius: 3, background: SEV_BG[alert.severity], color: sc }}>{alert.severity}</span>
          <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 7px', borderRadius: 3, background: st.bg, color: st.color }}>{st.label}</span>
          {alert.category && <CategoryBadge category={alert.category} size="xs" />}
          {alert.source   && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 7px', borderRadius: 3, background: `${SRC_COLORS[alert.source] || '#64748b'}18`, color: SRC_COLORS[alert.source] || '#64748b' }}>{alert.source}</span>}
          {alert.service  && <span className="text-xs mono" style={{ color: 'var(--text-3)' }}>{alert.service.slice(0, 24)}</span>}
          <span className="text-xs ml-auto" style={{ color: 'var(--text-3)' }}><Clock size={9} style={{ display: 'inline', marginRight: 3 }} />{timeAgo(alert.triggeredAt)}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col items-end gap-1 shrink-0" onClick={e => e.stopPropagation()}>
        <div className="flex gap-1">
          <button onClick={e => { e.stopPropagation(); onView(); }} title="View" style={{ padding: 5, borderRadius: 5, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-3)', cursor: 'pointer', lineHeight: 0 }}><Eye size={11} /></button>
          <button onClick={e => { e.stopPropagation(); onEdit(); }} title="Edit" style={{ padding: 5, borderRadius: 5, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-3)', cursor: 'pointer', lineHeight: 0 }}><Edit2 size={11} /></button>
          <button onClick={del} title="Delete" style={{ padding: 5, borderRadius: 5, background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.2)', color: '#f87171', cursor: 'pointer', lineHeight: 0 }}><Trash2 size={11} /></button>
        </div>
        {alert.status === 'OPEN' && (
          <div className="flex gap-1 mt-1">
            <button onClick={ack}     className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(245,158,11,.15)', color: '#fbbf24', border: '1px solid rgba(245,158,11,.3)', cursor: 'pointer' }}>Ack</button>
            <button onClick={resolve} className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(34,197,94,.15)',  color: '#4ade80', border: '1px solid rgba(34,197,94,.3)',  cursor: 'pointer' }}>Resolve</button>
          </div>
        )}
        {alert.status === 'ACKNOWLEDGED' && (
          <button onClick={resolve} className="text-xs px-2 py-0.5 rounded mt-1" style={{ background: 'rgba(34,197,94,.15)', color: '#4ade80', border: '1px solid rgba(34,197,94,.3)', cursor: 'pointer' }}>Resolve</button>
        )}
      </div>
    </div>
  );
}

// ── Main View ─────────────────────────────────────────────────────────────────
export default function AlertsView() {
  const { alerts, setAlerts, addAlert, updateAlert } = useStore();
  const [loading,    setLoading]    = useState(false);
  const [statusFilter, setStatus]  = useState('ALL');
  const [sevFilter,  setSev]       = useState('ALL');
  const [catFilter,  setCat]       = useState('ALL');
  const [search,     setSearch]    = useState('');
  const [viewAlert,  setViewAlert] = useState(null);
  const [editAlert,  setEditAlert] = useState(null);
  const [showCreate, setShow]      = useState(false);

  async function load() {
    setLoading(true);
    try {
      const data = await alertsApi.list({ status: 'ALL', limit: 200 });
      if (data && data.length > 0) setAlerts(data);
      else setAlerts(DEMO_ALERTS);
    } catch { setAlerts(DEMO_ALERTS); }
    finally { setLoading(false); }
  }

  useEffect(() => { if (alerts.length === 0) load(); }, []);

  function onSaved(saved, isEdit) { if (isEdit) updateAlert(saved); else addAlert(saved); }

  const filtered = alerts.filter(a => {
    if (statusFilter !== 'ALL' && a.status   !== statusFilter) return false;
    if (sevFilter    !== 'ALL' && a.severity !== sevFilter)    return false;
    if (catFilter    !== 'ALL' && a.category !== catFilter)    return false;
    if (search && !a.title.toLowerCase().includes(search.toLowerCase()) && !(a.description || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const counts = {
    total:    alerts.length,
    open:     alerts.filter(a => a.status    === 'OPEN').length,
    crit:     alerts.filter(a => a.severity  === 'CRITICAL' && a.status === 'OPEN').length,
    acked:    alerts.filter(a => a.status    === 'ACKNOWLEDGED').length,
    resolved: alerts.filter(a => a.status    === 'RESOLVED').length,
  };

  // Per-category open alert counts for the filter chips
  const catCounts = {};
  alerts.forEach(a => {
    if (a.status === 'OPEN') catCounts[a.category] = (catCounts[a.category] || 0) + 1;
  });

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-5 py-3 shrink-0 flex items-center gap-3 flex-wrap"
        style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
        <Bell size={18} style={{ color: counts.crit > 0 ? '#ef4444' : '#60a5fa' }} />
        <span className="text-sm font-semibold">Alert Center</span>
        <div className="flex gap-2 ml-2 flex-wrap">
          <StatChip label="Total"    value={counts.total}    color="#64748b" />
          <StatChip label="Open"     value={counts.open}     color="#ef4444" active={counts.open > 0} />
          <StatChip label="Critical" value={counts.crit}     color="#ef4444" active={counts.crit > 0} bold />
          <StatChip label="Ack'd"    value={counts.acked}    color="#f59e0b" />
          <StatChip label="Resolved" value={counts.resolved} color="#22c55e" />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={load}
            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', borderRadius: 6, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer', fontSize: 12 }}>
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={() => setShow(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500, background: 'rgba(59,130,246,.2)', color: '#60a5fa', border: '1px solid rgba(59,130,246,.3)', cursor: 'pointer' }}>
            <Plus size={14} /> New Alert
          </button>
        </div>
      </div>

      {/* Category filter bar */}
      <div className="px-5 py-2 shrink-0 flex items-center gap-2 flex-wrap"
        style={{ borderBottom: '1px solid var(--border)', background: 'rgba(0,0,0,.15)' }}>
        <Shield size={11} style={{ color: 'var(--text-3)' }} />
        <button onClick={() => setCat('ALL')}
          className="px-2.5 py-1 rounded-lg text-xs font-semibold"
          style={{ background: catFilter === 'ALL' ? 'rgba(255,255,255,.1)' : 'transparent', color: catFilter === 'ALL' ? 'var(--text)' : 'var(--text-3)', border: `1px solid ${catFilter === 'ALL' ? 'rgba(255,255,255,.15)' : 'transparent'}`, cursor: 'pointer' }}>
          All Categories
        </button>
        {Object.entries(CATEGORIES).map(([cat, meta]) => {
          const Icon   = meta.icon;
          const active = catFilter === cat;
          const cnt    = catCounts[cat] || 0;
          return (
            <button key={cat} onClick={() => setCat(active ? 'ALL' : cat)}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold"
              style={{ background: active ? `${meta.color}20` : 'transparent', color: active ? meta.color : 'var(--text-3)', border: `1px solid ${active ? meta.color + '40' : 'transparent'}`, cursor: 'pointer' }}>
              <Icon size={10} /> {cat}
              {cnt > 0 && <span style={{ fontSize: 9, fontWeight: 800, padding: '0px 5px', borderRadius: 20, background: `${meta.color}25`, color: meta.color }}>{cnt}</span>}
            </button>
          );
        })}
      </div>

      {/* Status + severity + search row */}
      <div className="px-5 py-2 shrink-0 flex items-center gap-2 flex-wrap"
        style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
        <div className="flex gap-1">
          {['ALL','OPEN','ACKNOWLEDGED','RESOLVED'].map(s => (
            <button key={s} onClick={() => setStatus(s)}
              className="px-2 py-1 rounded text-xs font-medium"
              style={{ background: statusFilter === s ? 'rgba(59,130,246,.2)' : 'var(--bg-tertiary)', color: statusFilter === s ? '#60a5fa' : 'var(--text-3)', border: `1px solid ${statusFilter === s ? 'rgba(59,130,246,.4)' : 'var(--border)'}`, cursor: 'pointer' }}>
              {s}
            </button>
          ))}
        </div>
        <div style={{ width: 1, height: 18, background: 'var(--border)' }} />
        <div className="flex gap-1">
          {['ALL','CRITICAL','WARNING','INFO'].map(sv => (
            <button key={sv} onClick={() => setSev(sv)}
              className="px-2 py-1 rounded text-xs font-medium"
              style={{ background: sevFilter === sv ? (sv === 'ALL' ? 'rgba(59,130,246,.2)' : SEV_BG[sv]) : 'var(--bg-tertiary)', color: sevFilter === sv ? (sv === 'ALL' ? '#60a5fa' : SEV_COLORS[sv]) : 'var(--text-3)', border: `1px solid ${sevFilter === sv ? (sv === 'ALL' ? 'rgba(59,130,246,.4)' : SEV_COLORS[sv] + '40') : 'var(--border)'}`, cursor: 'pointer' }}>
              {sv !== 'ALL' && `${SEV_ICONS[sv]} `}{sv}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-1 rounded px-2"
          style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}>
          <Search size={11} style={{ color: 'var(--text-3)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search alerts…"
            className="bg-transparent outline-none text-xs py-1 w-44" style={{ color: 'var(--text)' }} />
        </div>
        <span className="text-xs" style={{ color: 'var(--text-3)' }}>{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Alert list */}
      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2">
        {loading && <div className="text-sm text-center py-8" style={{ color: 'var(--text-3)' }}>Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Shield size={36} style={{ color: 'var(--text-3)' }} />
            <div className="text-sm font-medium">No alerts match the current filters</div>
            <button onClick={() => { setStatus('ALL'); setSev('ALL'); setCat('ALL'); setSearch(''); }}
              style={{ padding: '6px 14px', borderRadius: 6, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer', fontSize: 12 }}>
              Clear filters
            </button>
          </div>
        )}
        {filtered.map(a => (
          <AlertCard key={a._id} alert={a} onView={() => setViewAlert(a)} onEdit={() => setEditAlert(a)} />
        ))}
      </div>

      {/* Modals */}
      {viewAlert && <AlertDetail alert={viewAlert} onClose={() => setViewAlert(null)} onEdit={() => { setEditAlert(viewAlert); setViewAlert(null); }} />}
      {(editAlert || showCreate) && <AlertForm alert={editAlert || null} onClose={() => { setEditAlert(null); setShow(false); }} onSaved={onSaved} />}
    </div>
  );
}

function StatChip({ label, value, color, active, bold }) {
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs"
      style={{ background: active ? `${color}18` : 'var(--bg-tertiary)', border: `1px solid ${active ? color + '35' : 'var(--border)'}` }}>
      {active && <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, display: 'inline-block' }} />}
      <span style={{ color: 'var(--text-3)' }}>{label}</span>
      <span style={{ fontWeight: bold ? 800 : 600, color: active ? color : 'var(--text-2)' }}>{value}</span>
    </div>
  );
}
